import { ErrorModal } from "@/components/shared/modal/error-modal";
import Modal from "@/components/shared/modal/modal";
import { ComponentType } from "@/pages/configurations/componentTypes/models/ComponentType";
import { INITIAL_VALUES } from "@/pages/configurations/componentTypes/models/FormInitialValues";
import { useRootTypeContext } from "@/pages/configurations/rootTypes/context/RootTypeContext";
import {
	Button,
	CircularProgress,
	FormControl,
	FormGroup,
	FormLabel,
	Grid,
	InputLabel,
	NativeSelect,
	Snackbar,
	Switch,
	TextField,
	Theme,
	Typography,
	createStyles,
	makeStyles,
} from "@material-ui/core";
import { Alert, Color } from "@material-ui/lab";
import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import {
	getComponentTypeById,
	updateComponentType,
} from "../../repositories/ComponentTypes";

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		modalBody: {},
		title: {
			fontSize: 15,
		},
		chip: {
			margin: 2,
		},
		errorLabel: {
			fontSize: 12,
			color: "#F44336",
		},
	}),
);

type Props = {
	componentTypeIdForEdit: number | null;
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
};

export const EditComponentType = ({
	componentTypeIdForEdit,
	show,
	onHide,
}: Props) => {
	const classes = useStyles();
	const [openAlertMessage, setOpenAlertMessage] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [message, setMessage] = useState("");
	const [messageType, setMessageType] = useState<Color | undefined>(undefined);

	const [componentTypeForEdit, setComponentTypeForEdit] =
		useState<ComponentType | null>(null);

	const [error, setError] = useState<string | null>(null);

	const {
		state: { rootTypes },
	} = useRootTypeContext();

	const {
		control,
		setValue,
		handleSubmit,
		formState: { errors },
	} = useForm<ComponentType>({ defaultValues: INITIAL_VALUES });

	const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setOpenAlertMessage(false);
	};

	const onSubmit: SubmitHandler<ComponentType> = async (data) => {
		setIsSubmitLoading(true);
		try {
			const res = await updateComponentType(componentTypeIdForEdit, data);
			if (res.ok) {
				setMessage("The component type was updated successfully");
				setMessageType("success");
				onHide(true);
			}
		} catch (e) {
			setMessage(`Error, please try again`);
			setMessageType("error");
			setError(e as string);
		}
		setIsSubmitLoading(false);
		setOpenAlertMessage(true);
	};

	useEffect(() => {
		if (show) {
			(async () => {
				setIsLoading(true);
				const { componentType } = await getComponentTypeById(
					componentTypeIdForEdit,
				);
				if (componentType?.success) {
					setComponentTypeForEdit(componentType.result);
				} else {
					onHide(true);
					setMessage("Error, component type not found. Try again");
					setOpenAlertMessage(true);
					setComponentTypeForEdit(null);
				}

				setIsLoading(false);
			})();
		}
	}, [show]);

	useEffect(() => {
		if (show && componentTypeForEdit) {
			const {
				componentTypeId,
				componentTypeName,
				rootTypeId,
				isTracked,
				isRepair,
			} = componentTypeForEdit!;
			setValue("componentTypeId", componentTypeId);
			setValue("componentTypeName", componentTypeName);
			setValue("rootTypeId", rootTypeId);
			setValue("isRepair", isRepair);
			setValue("isTracked", isTracked);
		}
	}, [componentTypeForEdit]);

	return (
		<>
			<>
				<Modal
					open={show}
					handleClose={() => {
						onHide(true);
					}}
					id="editComponentType"
					title="Edit component type"
					maxWidth="md"
				>
					<form onSubmit={handleSubmit(onSubmit)}>
						<Modal.Content>
							{isLoading && (
								<Grid
									container
									justifyContent="center"
									alignItems="center"
									style={{ minHeight: "170px" }}
								>
									{/* <LazyLoading /> */}
									<CircularProgress size="3rem" />
								</Grid>
							)}

							{!isLoading && (
								<>
									<Grid container spacing={4}>
										<Grid item md={3} xs={12}>
											<Controller
												name="componentTypeId"
												rules={{
													required: true,
													validate: { positive: (v) => v != 0 },
												}}
												control={control}
												render={({ field }) => (
													<TextField
														disabled
														label="Component Type ID"
														value={field.value}
														onChange={field.onChange}
														variant="outlined"
														error={!!errors.componentTypeId}
														fullWidth
														margin="dense"
													/>
												)}
											/>

											{errors.componentTypeId?.type === "positive" && (
												<span className={classes.errorLabel}>
													Component Type ID must be different from 0
												</span>
											)}
											{errors.componentTypeId?.type === "required" && (
												<span className={classes.errorLabel}>
													Component Type ID is required
												</span>
											)}
										</Grid>
										<Grid item md={9} xs={12}>
											<Controller
												name="componentTypeName"
												rules={{ required: true }}
												control={control}
												render={({ field }) => (
													<TextField
														label="Name"
														value={field.value}
														onChange={field.onChange}
														variant="outlined"
														error={!!errors.componentTypeName}
														fullWidth
														margin="dense"
													/>
												)}
											/>

											{errors.componentTypeName?.type === "required" && (
												<span className={classes.errorLabel}>
													Name is required
												</span>
											)}
										</Grid>
										<Grid item md={6} xs={12}>
											<Controller
												name="rootTypeId"
												control={control}
												render={({ field }) => (
													<FormControl fullWidth>
														<InputLabel
															variant="standard"
															htmlFor="uncontrolled-native"
														>
															root type
														</InputLabel>
														<NativeSelect
															value={field.value}
															onChange={field.onChange}
														>
															{rootTypes.map((type) => {
																return (
																	<option
																		value={type.rootTypeId}
																		key={type.rootTypeId}
																	>
																		{type.rootTypeName}
																	</option>
																);
															})}
														</NativeSelect>
													</FormControl>
												)}
											/>
										</Grid>
										<Grid item>
											<Typography>Repair</Typography>
											<Controller
												name="isRepair"
												control={control}
												render={({ field }) => (
													<FormGroup>
														<Switch
															// className='switchBase'
															color="primary"
															defaultChecked={field.value}
															value={field.value}
															onChange={field.onChange}
														/>

														<FormLabel component="legend">
															{field.value ? "Yes" : "No"}
														</FormLabel>
													</FormGroup>
												)}
											/>
										</Grid>
										<Grid item>
											<Typography>Tracked</Typography>
											<Controller
												name="isTracked"
												control={control}
												render={({ field }) => (
													<FormGroup>
														<Switch
															// className='switchBase'
															color="primary"
															defaultChecked={field.value}
															value={field.value}
															onChange={field.onChange}
														/>

														<FormLabel component="legend">
															{field.value ? "Yes" : "No"}
														</FormLabel>
													</FormGroup>
												)}
											/>
										</Grid>
									</Grid>
								</>
							)}
						</Modal.Content>
						<Modal.Actions style={{ padding: "2rem" }}>
							<Grid container spacing={2} justifyContent="flex-end">
								<Grid item md={3} xs={12} style={{ margin: 0 }}>
									<Button
										fullWidth
										variant="contained"
										color="default"
										onClick={() => {
											onHide(false);
											setComponentTypeForEdit(null);
										}}
									>
										Cancel
									</Button>
								</Grid>
								<Grid item md={3} xs={12} style={{ margin: 0 }}>
									<Button
										fullWidth
										startIcon={
											isSubmitLoading ? <CircularProgress size="1rem" /> : <></>
										}
										disabled={isSubmitLoading}
										variant="contained"
										color="primary"
										type="submit"
									>
										Save
									</Button>
								</Grid>
							</Grid>
						</Modal.Actions>
					</form>
				</Modal>
				<Snackbar
					open={openAlertMessage}
					autoHideDuration={2500}
					onClose={handleClose}
				>
					<Alert severity={messageType} onClose={handleClose}>
						{message}
					</Alert>
				</Snackbar>
			</>
			<ErrorModal error={error} onHide={() => setError(null)} />
		</>
	);
};
